// shell/Topbar/Topbar.tsx — search + create + notification bell + messages.
// Account actions live in the bottom-left rail menu.

import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { IconButton } from '@/ui';
import { Icon } from '@/icons';
import { useMessages, useSession } from '@/hooks';
import { CountBadge } from '../CountBadge';
import { NotificationBell } from './NotificationBell';

const Header = styled.header`
  grid-column: 2 / 4;
  grid-row: 1;
  /* Lift the topbar (and its dropdowns) above the Center/RightRail grid siblings.
     The backdrop-filter below makes this a stacking context, which would otherwise trap
     the account Popover's z-index inside the topbar and let the later-painted RightRail
     cover it. position+z-index raises the whole topbar subtree above its row-2 siblings. */
  position: relative;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 20px;
  border-bottom: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme }) => theme.color.surfaceChrome};
`;

const SearchForm = styled.form`
  flex: 1;
  max-width: 440px;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 8px 14px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.color.surface};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  color: ${({ theme }) => theme.color.textMuted};
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  &::placeholder {
    color: ${({ theme }) => theme.color.textMuted};
  }
`;

const Actions = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CreateButton = styled(IconButton)`
  background: ${({ theme }) => `${theme.color.accentCyan}24`};
  border-color: transparent;
  color: ${({ theme }) => theme.color.textPrimary};
  &:hover {
    background: ${({ theme }) => `${theme.color.accentCyan}40`};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 3px;
  }
`;

const BadgeWrap = styled.div`
  position: relative;
  display: inline-flex;
  & > span:last-child:not(:first-child) {
    position: absolute;
    top: -2px;
    right: -4px;
    pointer-events: none;
  }
`;

// ── Guest actions (1.24.70) ───────────────────────────────────────────────────
// A signed-out visitor gets explicit doors, not a pretend account: no Create
// button, no notification bell, no messages badge, no "Guest" avatar chip.
// Both links carry ?next= back to the current route, same as SignupCtaBar.

const guestLinkBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 36px;
  padding: 8px 16px;
  border-radius: ${({ theme }) => theme.radius.md};
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition:
    background 120ms ease,
    color 120ms ease;
`;

const GuestSignIn = styled(Link)`
  ${guestLinkBase}
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: transparent;
  color: ${({ theme }) => theme.color.textPrimary};
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const GuestSignUp = styled(Link)`
  ${guestLinkBase}
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  border: 1px solid transparent;
  background: ${({ theme }) => theme.color.accentCyan};
  color: ${({ theme }) => theme.color.onAccent};
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

export interface TopbarProps {
  /** Opens the compose sheet. Without it the Create button is inert — which it was. */
  onCompose?: () => void;
}

export function Topbar({ onCompose }: TopbarProps = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { totalUnread } = useMessages();
  const { currentUser, isAuthenticated } = useSession();
  const [query, setQuery] = useState('');

  function onSearch(e: FormEvent) {
    e.preventDefault();
    const q = query.trim();
    navigate(q ? `/explore?q=${encodeURIComponent(q)}` : '/explore');
  }

  // 1.24.70: signed-out visitors don't have an account, so the topbar stops
  // pretending they do — search plus the two honest doors, nothing else.
  const next = encodeURIComponent(location.pathname + location.search);

  return (
    <Header>
      <SearchForm onSubmit={onSearch} role="search">
        <Icon name="search" size={16} />
        <SearchInput
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search TruXSocial"
          aria-label="Search TruXSocial"
        />
      </SearchForm>

      {isAuthenticated && currentUser ? (
        <Actions>
          <CreateButton icon="plus" label="Create" title="Create a post" onClick={onCompose} />

          <NotificationBell />

          <BadgeWrap>
            <IconButton icon="mail" label="Messages" onClick={() => navigate('/messages')} />
            {totalUnread > 0 && <CountBadge>{totalUnread}</CountBadge>}
          </BadgeWrap>
        </Actions>
      ) : (
        <Actions data-testid="topbar-guest-actions">
          <GuestSignIn to={`/signin?next=${next}`}>Sign in</GuestSignIn>
          <GuestSignUp to={`/signup?next=${next}`}>Create account</GuestSignUp>
        </Actions>
      )}
    </Header>
  );
}
